// components/MonthBeer.tsx

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getCurrentMonthlyEdition } from 'services/firebase/monthlyEditionService';
import type { MonthlyEdition } from 'utils/types/MonthlyEdition';
import { styles } from './styles';

const MonthBeer = () => {
  const [edition, setEdition] = useState<MonthlyEdition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentMonthlyEdition()
      .then(data => {
        if (!cancelled) {
          setEdition(data);
          setError(data ? null : 'Nenhuma edição do mês encontrada.');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Erro ao carregar as cervejas do mês.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          ...styles.container,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !edition) {
    return (
      <Box sx={styles.container}>
        <Typography color="text.secondary" textAlign="center">
          {error ?? 'Nenhuma edição do mês disponível.'}
        </Typography>
      </Box>
    );
  }

  const { beers, label } = edition;

  return (
    <Box sx={styles.container}>
      <Typography variant="h5" sx={styles.title}>
        {label}
      </Typography>
      <Grid container spacing={4}>
        {beers.map((beer, index) => (
          <Grid item xs={12} key={`beer-${index}`}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                borderRadius: 4,
                maxWidth: 900,
                mx: 'auto',
                backgroundColor: '#ffffff',
              }}
            >
              <Box sx={styles.imageWrapper}>
                <Box
                  component="img"
                  src={beer.image}
                  alt={beer.name}
                  sx={styles.image}
                />
              </Box>
              <Box
                sx={{
                  flex: 2,
                  backgroundColor: beer.backgroundColor,
                  color: '#fff',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderTopRightRadius: { xs: 0, sm: 16 },
                  borderBottomRightRadius: { xs: 0, sm: 16 },
                  borderBottomLeftRadius: { xs: 0, sm: 0 },
                  borderTopLeftRadius: { xs: 0, sm: 0 },
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {beer.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Origem:</strong> {beer.origin}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Cervejaria:</strong> {beer.brewery}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Estilo:</strong> {beer.style}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Teor Alcoólico:</strong> {beer.abv}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Coloração:</strong> {beer.color}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Amargor:</strong> {beer.bitterness}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Temperatura ideal:</strong> {beer.temp}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Harmonização:</strong> {beer.pairing}
                  </Typography>
                </Box>
                <Typography variant="body2">{beer.description}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
        {/*
          Card do copo do mes ocultado temporariamente.
          Bloco mantido comentado para facilitar reativacao futura.
        */}
        {/*
        {edition.glasses.map((glassItem, index) => (
          <Grid item xs={12} key={`glass-${index}`}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                borderRadius: 4,
                maxWidth: 900,
                mx: 'auto',
                backgroundColor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  minHeight: 200,
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box
                  component="img"
                  src={glassItem.image}
                  alt={glassItem.name}
                  sx={{
                    ...styles.image,
                    height: 'auto',
                    width: 'auto',
                    maxHeight: 180,
                    maxWidth: '80%',
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 2,
                  backgroundColor: '#8B4513',
                  color: '#fff',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderTopRightRadius: { xs: 0, sm: 16 },
                  borderBottomRightRadius: { xs: 0, sm: 16 },
                  borderBottomLeftRadius: { xs: 0, sm: 0 },
                  borderTopLeftRadius: { xs: 0, sm: 0 },
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {glassItem.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Volume:</strong> {glassItem.volume}
                  </Typography>
                </Box>
                <Typography variant="body2">{glassItem.description}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
        */}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          component={Link}
          to="/cervejas-anteriores"
          variant="outlined"
          sx={{
            borderColor: '#f96100',
            color: '#f96100',
            fontWeight: 700,
            '&:hover': {
              borderColor: '#d95400',
              bgcolor: 'rgba(249, 97, 0, 0.06)',
            },
          }}
        >
          Ver cervejas anteriores
        </Button>
      </Box>
    </Box>
  );
};

export default MonthBeer;
